import React from "react";
import { Link } from "react-router-dom";
import { Fingerprint, Dices, UserSearch, Users, FileCode2, LayoutGrid, ArrowRight } from "lucide-react";
import { Reveal } from "@/components/Reveal";

const allTools = [
  {
    icon: Fingerprint,
    to: "/tools/npi-validator",
    title: "NPI Validator",
    desc: "Validate any NPI — CMS Luhn checksum + live NPPES lookup. Single or bulk, with CSV export.",
  },
  {
    icon: Dices,
    to: "/tools/fake-npi-generator",
    title: "Fake NPI Generator",
    desc: "Generate checksum-valid test NPI numbers for development, QA, training, and demos.",
  },
  {
    icon: Users,
    to: "/tools/fake-patient-generator",
    title: "Fake Patient Generator",
    desc: "Generate synthetic patient records — names, DOBs, member IDs, never-issued SSNs — for testing and demos.",
  },
  {
    icon: FileCode2,
    to: "/tools/payer-specific-synthetic-837",
    title: "Synthetic 837 Generator",
    desc: "Generate complete payer-specific X12 837P/I/D test claim files with synthetic data.",
  },
  {
    icon: UserSearch,
    to: "/tools/npi-lookup-by-name",
    title: "NPI Lookup by Name",
    desc: "Find any provider or organization's NPI in the official CMS NPPES registry.",
  },
];

/**
 * "More free tools" cross-link strip shown at the bottom of every tool page.
 * Renders the other tools plus a link to the /tools hub.
 */
export const MoreTools = ({ current }) => {
  const others = allTools.filter((t) => t.to !== current);
  return (
    <section className="border-t border-slate-100 bg-slate-50 py-20" data-testid="more-tools">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <Reveal>
          <div className="flex flex-col items-start justify-between gap-4 sm:flex-row sm:items-end">
            <div>
              <span className="text-sm font-semibold uppercase tracking-widest text-brand-600">
                Keep exploring
              </span>
              <h2 className="mt-3 text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
                More free NPI tools
              </h2>
            </div>
            <Link
              to="/tools"
              data-testid="more-tools-all-link"
              className="group inline-flex items-center gap-2 text-sm font-semibold text-brand hover:text-brand-600"
            >
              <LayoutGrid className="h-4 w-4" />
              Browse all free tools
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </Link>
          </div>
        </Reveal>
        <div className="mt-10 grid grid-cols-1 gap-6 md:grid-cols-2">
          {others.map((t, i) => (
            <Reveal key={t.to} delay={i * 0.08}>
              <Link
                to={t.to}
                data-testid={`more-tools-card-${t.to.split("/").pop()}`}
                className="group flex h-full items-start gap-4 rounded-2xl border border-slate-200 bg-white p-6 transition-all duration-300 hover:-translate-y-1 hover:border-brand hover:shadow-lg hover:shadow-brand/10"
              >
                <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-brand-50 text-brand transition-colors group-hover:bg-brand group-hover:text-white">
                  <t.icon className="h-5 w-5" />
                </span>
                <span>
                  <span className="block text-base font-semibold text-slate-900">{t.title}</span>
                  <span className="mt-1 block text-sm leading-relaxed text-slate-600">{t.desc}</span>
                  <span className="mt-3 inline-flex items-center gap-1.5 text-sm font-semibold text-brand">
                    Open tool
                    <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-1" />
                  </span>
                </span>
              </Link>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};

export default MoreTools;
